import React, { useRef, useState, useEffect } from 'react';
import { Camera, Images, X, ScanLine } from 'lucide-react';
import { toast } from 'sonner';

const MAX_PAGES = 10;
const MAX_SIDE = 2000;

// Downscale phone photos to JPEG so uploads stay small and fast
const compress = (file) => new Promise((resolve) => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    const scale = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    canvas.toBlob(b => resolve(b ? new File([b], 'pagina.jpg', { type: 'image/jpeg' }) : file), 'image/jpeg', 0.85);
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
  img.src = url;
});

const isPdf = (f) => f.type === 'application/pdf' || f.name?.toLowerCase().endsWith('.pdf');

export const ScanPagesPicker = ({ onSingle, onMulti }) => {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [pages, setPages] = useState([]);

  useEffect(() => () => pages.forEach(p => URL.revokeObjectURL(p.preview)), [pages]);

  const addFiles = (list) => {
    const files = Array.from(list || []);
    if (!files.length) return;
    const pdf = files.find(isPdf);
    if (pdf) {
      if (files.length > 1 || pages.length) toast.info('Los PDF se escanean solos; se usará solo el PDF');
      return onSingle(pdf);
    }
    const room = MAX_PAGES - pages.length;
    if (files.length > room) toast.error(`Máximo ${MAX_PAGES} páginas por factura`);
    setPages(prev => [...prev, ...files.slice(0, room).map(file => ({ file, preview: URL.createObjectURL(file) }))]);
  };

  const remove = (i) => setPages(prev => prev.filter((_, idx) => idx !== i));

  const analyze = async () => {
    if (pages.length === 1) return onSingle(await compress(pages[0].file));
    onMulti(await Promise.all(pages.map(p => compress(p.file))));
  };

  return (
    <div className="space-y-4" data-testid="scan-pages-picker">
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="scan-camera-input"
        onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
      <input ref={galleryRef} type="file" accept="image/*,.pdf,application/pdf" multiple className="hidden" data-testid="scan-file-input"
        onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />

      <div className="grid grid-cols-2 gap-3">
        <button onClick={() => cameraRef.current?.click()} data-testid="scan-camera-button"
          className="h-28 border-2 border-dashed border-gray-300 rounded-2xl flex flex-col items-center justify-center gap-2 hover:border-blue-400 hover:bg-blue-50 transition-colors">
          <Camera size={26} className="text-blue-600" />
          <span className="text-sm font-medium text-gray-700">{pages.length ? 'Otra foto' : 'Hacer foto'}</span>
        </button>
        <button onClick={() => galleryRef.current?.click()} data-testid="scan-upload-button"
          className="h-28 border-2 border-dashed border-gray-300 rounded-2xl flex flex-col items-center justify-center gap-2 hover:border-blue-400 hover:bg-blue-50 transition-colors">
          <Images size={26} className="text-blue-600" />
          <span className="text-sm font-medium text-gray-700 text-center leading-tight">Elegir fotos<br /><span className="text-xs text-gray-500 font-normal">varias a la vez, o un PDF</span></span>
        </button>
      </div>

      {pages.length > 0 && (
        <>
          <div className="grid grid-cols-3 gap-2" data-testid="scan-pages-list">
            {pages.map((p, i) => (
              <div key={p.preview} className="relative aspect-[3/4] rounded-lg overflow-hidden border bg-gray-50">
                <img src={p.preview} alt={`Página ${i + 1}`} className="w-full h-full object-cover" />
                <span className="absolute bottom-1 left-1 text-[10px] font-bold bg-black/60 text-white px-1.5 py-0.5 rounded">Pág. {i + 1}</span>
                <button onClick={() => remove(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 flex items-center justify-center shadow" data-testid={`scan-page-remove-${i}`}>
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
          <button onClick={analyze} data-testid="scan-analyze-button"
            className="w-full h-11 rounded-xl bg-blue-600 text-white font-medium flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors">
            <ScanLine size={18} /> Analizar {pages.length === 1 ? 'factura' : `factura de ${pages.length} páginas`}
          </button>
        </>
      )}
      {!pages.length && <p className="text-xs text-gray-500 text-center">Si la factura tiene varias hojas, haz una foto de cada una y pulsa Analizar.</p>}
    </div>
  );
};
